import { Link, useNavigate } from 'react-router-dom';
import { Bell, History, LogOut, User as UserIcon } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import type { Notification } from '@/lib/types';
import { locationLabel } from '@/lib/locations';

// Top navigation shared by the chef-facing screens. Shows the wordmark, active
// location, and a right cluster of notification bell / order history / avatar.
export function ChefNav({ notifications }: { notifications?: Notification[] }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const unread = notifications?.length ?? 0;

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-canvas/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/app" className="shrink-0">
          <img src="/lucilles-logo.svg" alt="Lucille's" className="h-9 w-auto" />
        </Link>
        <div className="font-head text-sm font-extrabold uppercase tracking-[0.12em] text-brand">
          {user?.role === 'super_admin' ? 'Admin' : locationLabel(user?.locationIds[0])}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/app/backorders"
            className="relative rounded p-2 text-muted hover:bg-surface-2 hover:text-ink"
            aria-label="Back-order alerts"
          >
            <Bell className="h-5 w-5" />
            {unread > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber px-1 text-[10px] font-bold text-[#241a02]">
                {unread}
              </span>
            )}
          </Link>
          <Link to="/app/history" className="rounded p-2 text-muted hover:bg-surface-2 hover:text-ink" aria-label="Order history">
            <History className="h-5 w-5" />
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 rounded p-2 text-muted hover:bg-surface-2 hover:text-ink"
            aria-label="Log out"
          >
            <UserIcon className="h-5 w-5" />
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
